/**
 * game-ikebana.js — a vessel, three lines, and a guide you may ignore.
 *
 * THIS FILE OWNS FINGERS AND NOTHING ELSE.
 *
 * What an arrangement IS — whether a role is a line this game has, whether the
 * flower is one she owns, whether a length is inside the range, and what any
 * of it is worth — is decided by the worker. See backend/src/ikebana.js. The
 * page sends { role, plant, length, angle } and takes the answer as the truth.
 *
 * THE DRAWING IS THE LAB'S, bundled into arcade-ikebana-art.js from
 * art/ikebana-lab.mjs together with the worker's IKEBANA config, so the line
 * names, the vessels and the ends of the sliders on this page are the same
 * objects the worker checks against rather than a second list.
 *
 * ONE REDRAW, FROM ONE MODEL — the garden's rule, for the garden's reason:
 * every action asks the worker and repaints from the answer, so there is no
 * optimistic local state to drift out of step.
 *
 * THE PROPORTION HINT BESIDE EACH LINE IS ADVICE, NOT A VERDICT. It is the
 * worker's arithmetic repeated here so the number moves while she moves the
 * slider; the points come from the worker's own judge() and are shown as it
 * sent them. If the two ever disagree, the worker is right and this is a bug.
 *
 * CLICKS ARE DELEGATED ON THE ROOT, which is the one element no repaint
 * replaces.
 */
(function () {
  'use strict';

  var Art = window.IkebanaArt;
  var root = document.querySelector('[data-ikebana-game]');
  if (!root || !Art) return;

  var IK = Art.IKEBANA;
  var BASE = '/apps/arcade';
  var state = null;      // the worker's last word, and the only truth here
  var busy = false;
  var said = null;       // { text, kind } from the last arrangement

  /* What she is building. Lengths and angles start at the ghost's suggestion
     for whichever vessel is chosen; no flower is picked for her. */
  var pick = { vessel: null, ghost: true, stems: {}, jushi: [] };

  /* AND WHOSE SHELF SHE IS LOOKING AT, if anybody's. Both of these are
     SOFT: the friends list is the newest thing on this page and the least
     important of them, so a /friends that never answers must cost her the
     list and never the arranging table. */
  var friends = null;      // null while it is still being asked for
  var visiting = null;     // a friend's shelf, or null for her own table

  async function api(path, body) {
    var res = await fetch(BASE + path, {
      method: body ? 'POST' : 'GET',
      headers: body
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    });
    var data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (!res.ok) {
      var err = new Error((data && data.message) || 'That did not work.');
      /* `error`, NOT `code` — the worker serialises every refusal as
         { error: <code>, message }. game-garden.js records at length how a
         whole branch of its error handling was dead because it read `code`. */
      err.code = (data && data.error) || ('http_' + res.status);
      throw err;
    }
    return data;
  }

  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  };

  /* NEVER SHOW A CHILD A CODE. HttpError does `super(message || code)`, so a
     refusal thrown without a sentence arrives with its message equal to its
     code — which is how a guest was once shown the word "login_required". */
  var HUMAN = {
    login_required: 'Sign in to arrange — your vessels and your flowers are kept with your account.',
    not_found: 'The arranging table is not open yet.',
    server_error: 'Something went wrong at our end. Try again in a moment.',
  };
  function humanise(err) {
    if (!err) return 'That did not work.';
    if (err.message && err.message !== err.code) return err.message;
    return HUMAN[err.code] || 'That did not work.';
  }

  /* ---- the model the page reads --------------------------------------- */
  var lineDef = function (key) {
    for (var i = 0; i < IK.lines.length; i++) if (IK.lines[i].key === key) return IK.lines[i];
    return null;
  };
  var vesselNow = function () {
    var list = (state && state.vessels) || [];
    for (var i = 0; i < list.length; i++) if (list[i].key === pick.vessel) return list[i];
    return list[0] || null;
  };
  var plantName = function (key) {
    var list = (state && state.plants) || [];
    for (var i = 0; i < list.length; i++) if (list[i].plant === key) return list[i].name;
    return key;
  };

  /* Start the three lines at the suggestion for this vessel: shin from the
     vessel's own size, then each one its fraction of the one before. */
  function seedLengths(vesselKey) {
    var ghost = Art.ghostStems(vesselKey);
    for (var i = 0; i < ghost.length; i++) {
      var g = ghost[i];
      var have = pick.stems[g.role] || {};
      pick.stems[g.role] = {
        plant: have.plant || null,
        length: Math.round(g.length),
        angle: g.angle,
      };
    }
  }

  /* ONE WAY TO NAME A STEM, whichever kind it is. The three lines are keyed by
     their own name; a supporting stem is `jushi:0`, `jushi:1` and so on, which
     is its position in the list rather than anything the worker knows about —
     every jushi is sent with the role 'jushi' and the worker does not care
     which is which. Everything that touches a stem goes through here, so the
     pickers and the sliders do not need to know the difference. */
  var JUSHI = 'jushi:';
  function stemAt(role) {
    if (String(role).indexOf(JUSHI) === 0) return pick.jushi[Number(role.slice(JUSHI.length))] || null;
    return pick.stems[role] || null;
  }

  /* The rows, in the shape the worker takes and the lab draws. Only lines with
     a flower in them are stems; the rest are not yet part of the arrangement. */
  function stemRows() {
    var out = [];
    for (var i = 0; i < IK.lines.length; i++) {
      var k = IK.lines[i].key, s = pick.stems[k];
      if (s && s.plant) out.push({ role: k, plant: s.plant, length: s.length, angle: s.angle });
    }
    for (var j = 0; j < pick.jushi.length; j++) {
      var u = pick.jushi[j];
      if (u && u.plant) out.push({ role: 'jushi', plant: u.plant, length: u.length, angle: u.angle });
    }
    return out;
  }

  /* THE HINT. The worker's inProportion(), repeated so the number moves under
     her finger. Advisory — see the note at the top of this file. */
  function proportion(key) {
    var def = lineDef(key);
    if (!def || !def.of) return null;
    var mine = pick.stems[key], parent = pick.stems[def.of];
    if (!mine || !parent || !parent.plant || !mine.plant) return null;
    var want = parent.length * def.ratio;
    return { want: want, near: Math.abs(mine.length - want) <= want * IK.tolerance };
  }

  /* A NEW SUPPORTING STEM. Jushi are never scored — the config is explicit
     that they are "what you add because it looks right, which is not a rule" —
     so there is no correct length or angle to start one at. It starts short
     against the shin, because that is what makes it read as supporting rather
     than as a fourth line, and at an angle that is deliberately none of the
     three the ghost suggests. She moves it from there. */
  var JUSHI_START = [32, 58, 22, 68, 45];
  function addJushi() {
    var shin = pick.stems.shin || {};
    var len = Math.round((shin.length || IK.stem.max / 2) * 0.45);
    pick.jushi.push({
      plant: null,
      length: Math.max(IK.stem.min, Math.min(IK.stem.max, len)),
      angle: JUSHI_START[pick.jushi.length % JUSHI_START.length],
    });
  }

  /* Whether there is a flower left to put in a new one. Offering an Add that
     can only produce an empty stem is offering nothing. */
  function sparePlant() {
    var held = state.flowers || {}, w = wanted();
    for (var k in held) if (held[k] - (w[k] || 0) > 0) return true;
    return false;
  }

  /* How many of each flower this arrangement would cut, so a picker can grey
     out one she has already spent. */
  function wanted() {
    var w = {}, rows = stemRows();
    for (var i = 0; i < rows.length; i++) w[rows[i].plant] = (w[rows[i].plant] || 0) + 1;
    return w;
  }

  /* ---- html ------------------------------------------------------------ */
  function vesselsHtml() {
    return '<div class="ik__vessels">' + state.vessels.map(function (v) {
      var on = v.key === pick.vessel;
      return '<button type="button" class="ik__vessel" data-vessel="' + esc(v.key) + '"'
        + ' aria-pressed="' + (on ? 'true' : 'false') + '"' + (v.has ? '' : ' disabled')
        + '>' + esc(v.name) + ' <span class="jp">' + esc(v.jp) + '</span>'
        + (v.has ? '' : '<small>after ' + v.need + ' arrangements</small>')
        + '</button>';
    }).join('') + '</div>';
  }

  function flowersHtml(role, chosen) {
    var list = state.plants || [], held = state.flowers || {}, w = wanted();
    if (!list.length) return '';
    return '<div class="ik__flowers">' + list.map(function (p) {
      var have = held[p.plant] || 0;
      var mineAlready = chosen === p.plant ? 1 : 0;
      /* she can keep the one she has already chosen for this line even when
         the rest of the arrangement has used the last of them */
      var spare = have - ((w[p.plant] || 0) - mineAlready);
      var on = chosen === p.plant;
      return '<button type="button" class="ik__flower" data-role="' + esc(role) + '"'
        + ' data-plant="' + esc(p.plant) + '" aria-pressed="' + (on ? 'true' : 'false') + '"'
        + (spare > 0 || on ? '' : ' disabled') + '>'
        + esc(p.name) + ' <small>×' + have + '</small></button>';
    }).join('')
      + (chosen ? '<button type="button" class="ik__flower" data-role="' + esc(role)
        + '" data-plant="">Take it out</button>' : '')
      + '</div>';
  }

  function sliderHtml(role, what, value, min, max) {
    var id = 'ik-' + role + '-' + what;
    return '<label class="ik__slider" for="' + id + '"><span>' + (what === 'length' ? 'Length' : 'Angle')
      + '</span><input id="' + id + '" type="range" min="' + min + '" max="' + max + '" step="1"'
      + ' value="' + value + '" data-role="' + esc(role) + '" data-what="' + what + '">'
      + '<output>' + value + (what === 'angle' ? '°' : '') + '</output></label>';
  }

  function lineHtml(def) {
    var s = pick.stems[def.key] || {};
    var p = proportion(def.key);
    var hint = '';
    if (p) {
      hint = '<span class="ik__hint" data-near="' + (p.near ? 'yes' : 'no') + '">'
        + 'three-quarters of ' + esc(lineDef(def.of).name) + ' is ' + Math.round(p.want) + '</span>';
    } else if (!def.of) {
      hint = '<span class="ik__hint">any length you like</span>';
    }
    return '<div class="ik__line"><div class="ik__lineHead">'
      + '<span class="ik__lineName">' + esc(def.name) + ' <span class="jp">' + esc(def.jp) + '</span></span>'
      + '<span class="ik__means">' + esc(def.means) + '</span>' + hint + '</div>'
      + flowersHtml(def.key, s.plant || null)
      + (s.plant ? sliderHtml(def.key, 'length', s.length, IK.stem.min, IK.stem.max)
        + sliderHtml(def.key, 'angle', s.angle, 0, 90) : '')
      + '</div>';
  }

  function jushiHtml() {
    var max = (IK.jushi && IK.jushi.max) || 0;
    var rows = pick.jushi.map(function (u, i) {
      var role = JUSHI + i;
      return '<div class="ik__line ik__line--jushi"><div class="ik__lineHead">'
        + '<span class="ik__lineName">Jushi <span class="jp">従枝</span></span>'
        + '<span class="ik__means">a stem that helps</span>'
        + '<button type="button" class="ik__drop" data-drop-jushi="' + i + '">Remove</button></div>'
        + flowersHtml(role, u.plant || null)
        + (u.plant ? sliderHtml(role, 'length', u.length, IK.stem.min, IK.stem.max)
          + sliderHtml(role, 'angle', u.angle, 0, 90) : '')
        + '</div>';
    }).join('');

    var room = pick.jushi.length < max;
    var spare = sparePlant();
    return '<div class="ik__jushi">' + rows
      + '<button type="button" class="ik__add" data-add-jushi'
      + (room && spare ? '' : ' disabled') + '>Add a supporting stem</button>'
      + '<p class="ik__aside">'
      + (!room ? 'That is as many as one arrangement takes.'
        : !spare ? 'Every flower you have is already in it.'
          : 'Extra stems, as many as ' + max + '. They are never scored — they are what you add'
            + ' because it looks right.')
      + '</p></div>';
  }

  function friendsHtml() {
    if (!friends) return '';
    if (!friends.length) {
      return '<section class="ik"><h2>Show somebody</h2>'
        + '<p class="ik__aside">No friends yet. Add one in the friends panel and you can'
        + ' look at each other&rsquo;s arrangements.</p></section>';
    }
    return '<section class="ik"><h2>Their arrangements</h2>'
      + '<div class="ik__flowers">' + friends.map(function (f) {
        return '<button type="button" class="ik__flower" data-visit="' + esc(f.id) + '"'
          + ' aria-pressed="' + (visiting && visiting.id === f.id ? 'true' : 'false') + '">'
          + esc(f.handle) + '</button>';
      }).join('') + '</div>'
      + (visiting ? visitingHtml() : '')
      + '</section>';
  }

  function visitingHtml() {
    var v = visiting;
    var head = '<div class="ik__lineHead" style="margin-top:12px">'
      + '<span class="ik__lineName">' + esc(v.handle) + '&rsquo;s shelf</span>'
      + '<span class="ik__means">' + (v.made === 1 ? 'one arrangement' : v.made + ' arrangements')
      + '</span>'
      + '<button type="button" class="ik__drop" data-leave>Close</button></div>';
    if (!v.recent.length) {
      return head + '<p class="ik__aside">Nothing on it yet.</p>';
    }
    /* NO NUMBER UNDER ANY OF THESE. Her own shelf shows what each one earned;
       this one shows the arrangement and stops there. The worker does not send
       the points, and this is the other half of that decision. */
    return head + '<div class="ik__recent">' + v.recent.map(function (w) {
      /* THE ONE THING A VISITOR CAN DO HERE, and it does not change the
         arrangement or pay anybody anything. Once said it stops being a
         button, because saying it again is the same nice one. */
      var act = w.saidNice
        ? '<span class="ik__nice ik__nice--mine">you said nice one</span>'
        : '<button type="button" class="ik__niceBtn" data-nice="' + esc(w.id) + '"'
          + (busy ? ' disabled' : '') + '>Nice one</button>';
      return '<figure>' + Art.sceneSvg(w.vessel, w.stems, { ghost: null, key: 'f-' + w.id })
        + '<figcaption>' + act + '</figcaption></figure>';
    }).join('') + '</div>';
  }

  /* "Yuki said nice one" / "Yuki and Mei said nice one" / and then the rest by
     name too. It never becomes "and 3 others", because that is a count. */
  function niceLine(names) {
    var n = names.slice();
    var who = n.length === 1 ? n[0]
      : n.slice(0, -1).join(', ') + ' and ' + n[n.length - 1];
    return who + ' said nice one';
  }

  function recentHtml() {
    if (!state.recent || !state.recent.length) return '';
    return '<section class="ik"><h2>What you have made</h2>'
      + '<div class="ik__recent">' + state.recent.map(function (w) {
        /* THE KEY IS THE WORK'S OWN ID. A dozen of these are on screen at
           once and every kago would otherwise carry the same clipPath id. */
        /* NAMES, NEVER A NUMBER. The worker sends who said nice one and no
           count, and this is the other half of that: "Yuki said nice one",
           not "2". This game has no score and a tally beside a picture is one. */
        var nice = (w.nice || []).length
          ? '<span class="ik__nice">' + esc(niceLine(w.nice)) + '</span>' : '';
        return '<figure>' + Art.sceneSvg(w.vessel, w.stems, { ghost: null, key: w.id })
          + '<figcaption>' + (w.points ? '+' + w.points : 'made') + nice
          + '</figcaption></figure>';
      }).join('') + '</div></section>';
  }

  function render() {
    if (!state) { root.innerHTML = '<section class="ik"><p class="ik__note">Setting the table…</p></section>'; return; }

    if (state.guest) {
      root.innerHTML = '<section class="ik"><h2>Ikebana <span class="jp">生け花</span></h2>'
        + '<p class="ik__note">' + esc(HUMAN.login_required) + '</p>'
        + '<div class="ik__stage">' + Art.sceneSvg('suiban', [], { ghost: true }) + '</div>'
        + '<p class="ik__note">Three lines — shin, soe and hikae — each three-quarters of the one'
        + ' before. The dashed guide shows where they usually go, and you may ignore it.</p>'
        + '</section>';
      return;
    }

    var kinds = Object.keys(state.flowers || {}).filter(function (k) { return state.flowers[k] > 0; });
    var rows = stemRows();
    var v = vesselNow();

    /* THE PICTURE AND THE CONTROLS ARE ONE THING NOW.
       Barb: "the controls are under the game which means I have to scroll up
       and down." They were: a column of the drawing, then the vessels, then
       three lines of flowers and sliders, then the buttons — so by the time
       she was lengthening a stem the arrangement she was lengthening it in
       had scrolled off the top. Every slider was a guess followed by a scroll
       to see what it did.
       The two are wrapped together here so the stylesheet can hold the
       picture still while the controls move under it, and stand them side by
       side when there is room for two columns. */
    var html = '<section class="ik">'
      + '<div class="ik__top"><h2>Ikebana <span class="jp">生け花</span></h2>'
      + '<span class="ik__made">' + (state.made ? state.made + ' made' : 'nothing made yet') + '</span></div>'
      + '<div class="ik__body">'
      + '<div class="ik__stage">'
      + Art.sceneSvg(v ? v.key : 'suiban', rows, { ghost: pick.ghost ? true : null }) + '</div>'
      + '<div class="ik__controls">'
      + vesselsHtml();

    /* AN EMPTY GAME STILL SHOWS ITS CONTROLS.
       Barb: "controls missing on mobile" — and they were not missing, they
       were never drawn: with no cut flowers the whole of shin, soe and hikae
       was replaced by one sentence, so the page ended at the vessels and
       looked broken rather than empty. A game that hides its own controls
       when there is nothing to put in them is indistinguishable from a game
       that has lost them.

       So the three lines are always there, with their names, their meanings
       and the proportion each one is held to; what an empty basket takes away
       is the flowers to choose and the sliders that only exist once a flower
       is in the line. The sentence says why, above them rather than instead
       of them. */
    if (!kinds.length) {
      /* AND BOTH WAYS OUT ARE DOORS, not directions.
         Barb: "tell them that the markets can be found by traveling and link
         it to the omiyage game." A child who reads "buy one at a market" and
         cannot see a market anywhere is being told about a place, not shown
         the way to it — the markets are not on this page or in the garden,
         they are at the stations, which is a thing you have to already know
         to go looking for. */
      html += '<p class="ik__empty">No cut flowers yet &mdash; so there is nothing to '
        + 'put in these three lines. Chrysanthemum, morning glory and nanohana can all '
        + 'be arranged, and there are two ways to get one.</p>'
        + '<p class="ik__empty">Grow it yourself in <a href="/pages/arcade-garden">your '
        + 'garden</a> and pick it when it is ready. Or buy one already cut at a flower '
        + 'market &mdash; the markets are in the cities, so you get to one by '
        + 'travelling: take the train in <a href="/pages/arcade-omiyage">Omiyage '
        + 'Atsume</a>, and when you arrive at a station tap <b>Market</b>. Each city '
        + 'sells a different three.</p>';
    }
    html += IK.lines.map(lineHtml).join('');
    if (kinds.length) html += jushiHtml();
    html += '<div class="ik__row">'
      + '<button type="button" class="ik__go" data-make'
      + (kinds.length && rows.length && !busy ? '' : ' disabled') + '>'
      + (busy ? 'Arranging…' : 'Arrange it') + '</button>'
      + '<button type="button" class="ik__ghost" data-ghost aria-pressed="'
      + (pick.ghost ? 'true' : 'false') + '">' + (pick.ghost ? 'Hide the guide' : 'Show the guide')
      + '</button></div>';
    if (said) html += '<p class="ik__said ik__said--' + said.kind + '">' + esc(said.text) + '</p>';
    html += '</div></div></section>' + recentHtml() + friendsHtml();
    root.innerHTML = html;
  }

  /* ---- doing things ---------------------------------------------------- */
  async function load() {
    try {
      state = await api('/ikebana');
      if (!pick.vessel) {
        var first = (state.vessels || []).filter(function (x) { return x.has; })[0];
        pick.vessel = first ? first.key : 'suiban';
        seedLengths(pick.vessel);
      }
    } catch (err) {
      state = null;
      root.innerHTML = '<section class="ik"><p class="ik__said ik__said--bad">'
        + esc(humanise(err)) + '</p></section>';
      return;
    }
    render();
  }

  async function make() {
    if (busy) return;
    var rows = stemRows();
    if (!rows.length) return;
    busy = true; said = null; render();
    try {
      var out = await api('/ikebana/make', { vessel: pick.vessel, stems: rows });
      said = { text: out.note + (out.points ? '  +' + out.points : ''), kind: 'ok' };
      /* A FAMILIAR TAGGED HERE CAME BACK WITH SOMETHING. The OBJECT the worker
         sends, not a game name — see the note where the garden's hook used to
         be. Soft: a toast that fails must never look like a lost arrangement. */
      if (out.companion && window.Arcade && window.Arcade.companion) {
        try { window.Arcade.companion(out.companion); } catch (e2) { /* not today */ }
      }
      /* The flowers are spent and the count has moved, so the worker's home is
         stale in exactly the way this page must not be. Ask again. */
      state = await api('/ikebana');
      /* Keep the lines she set, but drop any flower she no longer has. */
      for (var i = 0; i < IK.lines.length; i++) {
        var k = IK.lines[i].key, s = pick.stems[k];
        if (s && s.plant && !(state.flowers || {})[s.plant]) s.plant = null;
      }
      /* A supporting stem she never put a flower in is still one she asked for,
         so it stays; one whose flower has been used up loses the flower rather
         than the stem, the same as a named line above. */
      pick.jushi.forEach(function (u) {
        if (u.plant && !(state.flowers || {})[u.plant]) u.plant = null;
      });
    } catch (err) {
      said = { text: humanise(err), kind: 'bad' };
    }
    busy = false;
    render();
  }

  async function nice(workId) {
    if (busy || !visiting) return;
    busy = true; said = null; render();
    try {
      await api('/ikebana/nice', { id: workId });
      /* Marked from the worker's answer rather than assumed here, the same
         rule the rest of this page follows: ask, then repaint from what came
         back. */
      visiting = await api('/ikebana/friend?id=' + encodeURIComponent(visiting.id));
    } catch (err) {
      said = { text: humanise(err), kind: 'bad' };
    }
    busy = false;
    render();
  }

  async function visit(id) {
    if (busy) return;
    busy = true; said = null; render();
    try {
      visiting = await api('/ikebana/friend?id=' + encodeURIComponent(id));
    } catch (err) {
      visiting = null;
      said = { text: humanise(err), kind: 'bad' };
    }
    busy = false;
    render();
  }

  root.addEventListener('click', function (e) {
    var t = e.target.closest('button');
    if (!t || t.disabled) return;

    if (t.hasAttribute('data-vessel')) {
      pick.vessel = t.getAttribute('data-vessel');
      seedLengths(pick.vessel);
      said = null; render(); return;
    }
    if (t.hasAttribute('data-plant')) {
      var role = t.getAttribute('data-role'), plant = t.getAttribute('data-plant');
      var s = stemAt(role);
      if (s) s.plant = plant || null;
      said = null; render(); return;
    }
    if (t.hasAttribute('data-add-jushi')) { addJushi(); said = null; render(); return; }
    if (t.hasAttribute('data-drop-jushi')) {
      pick.jushi.splice(Number(t.getAttribute('data-drop-jushi')), 1);
      said = null; render(); return;
    }
    if (t.hasAttribute('data-nice')) { nice(t.getAttribute('data-nice')); return; }
    if (t.hasAttribute('data-visit')) { visit(t.getAttribute('data-visit')); return; }
    if (t.hasAttribute('data-leave')) { visiting = null; render(); return; }
    if (t.hasAttribute('data-ghost')) { pick.ghost = !pick.ghost; render(); return; }
    if (t.hasAttribute('data-make')) { make(); return; }
  });

  /* SLIDERS REPAINT THE PICTURE AND NOTHING ELSE. A full render() on every
     input event would replace the very range input her finger is on, which on
     a phone ends the drag. So the stage and the hint are patched in place and
     the inputs are left exactly where they are. */
  root.addEventListener('input', function (e) {
    var el = e.target;
    if (!el || el.type !== 'range') return;
    var role = el.getAttribute('data-role'), what = el.getAttribute('data-what');
    var s = stemAt(role);
    if (!s) return;
    s[what] = Number(el.value);
    var out = el.parentNode.querySelector('output');
    if (out) out.textContent = el.value + (what === 'angle' ? '°' : '');

    var stage = root.querySelector('.ik__stage');
    var v = vesselNow();
    if (stage) stage.innerHTML = Art.sceneSvg(v ? v.key : 'suiban', stemRows(),
      { ghost: pick.ghost ? true : null });

    for (var i = 0; i < IK.lines.length; i++) {
      var def = IK.lines[i];
      var p = proportion(def.key);
      if (!p) continue;
      var host = root.querySelector('[data-role="' + def.key + '"][data-what="length"]');
      var head = host && host.closest('.ik__line').querySelector('.ik__hint');
      if (head) {
        head.setAttribute('data-near', p.near ? 'yes' : 'no');
        head.textContent = 'three-quarters of ' + lineDef(def.of).name + ' is ' + Math.round(p.want);
      }
    }
  });

  load();

  /* THE FRIENDS LIST, ASKED FOR SEPARATELY AND SOFTLY. It comes from /friends,
     which every other page in this arcade already uses, rather than a second
     list of names kept here — and a /friends that fails or never answers must
     cost her the list and never the arranging table. */
  (async function () {
    try {
      var f = await api('/friends');
      friends = (f && f.friends ? f.friends : [])
        .map(function (x) { return { id: x.id, handle: x.handle }; })
        .filter(function (x) { return x.id && x.handle; });
    } catch (e) { friends = []; }
    if (state) render();
  }());

  /* NO FAMILIAR HOOK HERE, ON PURPOSE. game-garden.js has one and it does
     nothing: Arcade.companion takes the companion OBJECT a worker sends back
     — every other game passes `res.companion` — and the garden passes the
     string 'garden', which companionToast() drops on its first line because a
     string has no `.food`. Copying it here would have been a second call that
     looks like a feature. config.js says a familiar tagging along to a calm
     game is a thing to add deliberately; when it is added, it is added there
     and the response carries it. */
}());
